import Microgame from '../engine/Microgame.js';

export default class KiteGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Cerf-volant !`, en: `Kite!`, es: `¡Cometa!` },
            controls: { fr: `Appuie frénétiquement pour prendre de la hauteur`, en: `Tap fast to gain altitude`, es: `Toca rápido para ganar altura` },
            objective: { fr: `Garde le cerf-volant en l'air au-dessus de la ligne`, en: `Keep the kite in the air above the line`, es: `Mantén la cometa en el aire por encima de la línea` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" stroke-width="2"><path d="m12 2 4 8-4 12-4-12 4-8z"/><path d="M12 22s-2 2-2 2"/></svg>`,
            actions: [
                { type: 'hold', x: 50, y: 68, holdDuration: 300 },
                { type: 'wait', duration: 120 },
                { type: 'hold', x: 48, y: 66, holdDuration: 300 },
                { type: 'wait', duration: 120 },
                { type: 'hold', x: 52, y: 64, holdDuration: 300 },
                { type: 'wait', duration: 120 },
                { type: 'hold', x: 50, y: 62, holdDuration: 300 },
                { type: 'wait', duration: 120 },
                { type: 'hold', x: 49, y: 60, holdDuration: 300 },
                { type: 'wait', duration: 520 },
            ],
            loopDuration: 3400
        };
    }
    constructor(engine) {
        super(engine);
        this.kite = null;
        this.canvas = null;
        this.ctx = null;

        // Physics
        this.kiteX = 0;
        this.kiteY = 0; // position in px
        this.kiteVX = 0;
        this.kiteVY = 0;
        this.kiteAngle = 0;
        this.gravity = 0.12;
        this.drag = 0.985; // more damping = smoother

        // Gusts
        this.gusts = []; // { x, y, vx, vy, radius, life, maxLife }

        const speedMult = engine.speedMultiplier || 1.0;
        const adj = 1 + (speedMult - 1) * 0.35;
        this.gravity = 0.15 + adj * 0.08 + (speedMult * 0.04); // much stronger gravity

        // GameEngine divides this.duration by speedMult. 
        // We want real-time survival to be 4500ms at x1, and exactly 3000ms at x4.
        const realTimeMs = 4500 - (speedMult - 1) * 500;
        this.duration = realTimeMs * speedMult;

        // Storm cross wind (gentle push left or right)
        this.crossWind = (this.isPlayback ? 1 : (Math.random() > 0.5 ? 1 : -1)) * (0.03 + adj * 0.025);
        this.stringSnapped = false;
    }

    triggerSnap() {
        if (this.stringSnapped) return;
        this.stringSnapped = true;
        this.lose();
        // Give kite a chaotic tumble velocity
        this.kiteVX = this.crossWind * 20 + (Math.random() - 0.5) * 10;
        this.kiteVY = -5 - Math.random() * 5;
    }

    onTimeout() {
        // Survive the timer → win
        return true;
    }

    setup() {
        this.engine.ui.instruction.textContent = "FLY!";

        this.canvas = document.createElement('canvas');
        this.canvas.style.position = 'absolute';
        this.canvas.style.top = '0';
        this.canvas.style.left = '0';
        this.canvas.style.width = '100%';
        this.canvas.style.height = '100%';

        const rect = this.container.getBoundingClientRect();
        this.W = rect.width || 400;
        this.H = rect.height || 700;
        this.canvas.width = this.W;
        this.canvas.height = this.H;
        this.container.appendChild(this.canvas);
        this.ctx = this.canvas.getContext('2d');

        // Start kite centered, upper third
        this.kiteX = this.W * 0.5;
        this.kiteY = this.H * 0.3;
        this.kiteVY = 2; // Initial downward push so it starts falling immediately

        this.onDown = this.onDown.bind(this);
        this.onMove = this.onMove.bind(this);

        this.engine.input.element.addEventListener('pointerdown', this.onDown);
        this.engine.input.element.addEventListener('pointermove', this.onMove);

        this.lastTime = null;
        this.animLoop();
    }

    // Create a gust (air burst) at tap/move position
    createGust(clientX, clientY) {
        const rect = this.container.getBoundingClientRect();
        const gx = clientX - rect.left;
        const gy = clientY - rect.top;

        // Direction: from finger toward kite
        const dx = this.kiteX - gx;
        const dy = this.kiteY - gy;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const speed = 1.5; // gentle push
        
        this.gusts.push({
            x: gx, y: gy,
            vx: (dx / dist) * speed,
            vy: (dy / dist) * speed - 0.5, // mild upward bias
            radius: 30 + Math.random() * 20,
            life: 0,
            maxLife: 30 + Math.random() * 15
        });
    }

    onDown(e) {
        if (this.won || this.lost) return;
        this.createGust(e.clientX, e.clientY);
    }

    onMove(e) {
        if (this.won || this.lost) return;
        // Only create gusts if finger is pressed (buttons > 0 for pointer events)
        if (e.buttons > 0 || e.pressure > 0) {
            this.createGust(e.clientX, e.clientY);
        }
    }

    animLoop() {
        if (!this.active) return;

        const now = performance.now();
        if (!this.lastTime) this.lastTime = now;
        const dt = Math.min((now - this.lastTime) / 16.67, 3); // normalized to ~60fps
        this.lastTime = now;

        if (this.stringSnapped) {
            this.kiteVY += this.gravity * 2 * dt;
            this.kiteX += this.kiteVX * dt;
            this.kiteY += this.kiteVY * dt;
            this.kiteAngle += this.kiteVX * 2 * dt;
            this.draw();
            requestAnimationFrame(() => this.animLoop());
            return;
        }

        // Gravity & Crosswind
        this.kiteVY += this.gravity * dt;
        this.kiteVX += this.crossWind * dt;

        // Update gusts and apply forces to kite
        for (let i = this.gusts.length - 1; i >= 0; i--) {
            const g = this.gusts[i];
            g.x += g.vx * dt;
            g.y += g.vy * dt;
            g.radius += 0.4 * dt;
            g.life += dt;

            // Force on kite: inverse-square proximity
            const dx = this.kiteX - g.x;
            const dy = this.kiteY - g.y;
            const dist = Math.sqrt(dx * dx + dy * dy) || 1;

            if (dist < g.radius * 2.5) {
                const force = Math.max(0, 1 - dist / (g.radius * 2.5)) * 0.18; // very gentle
                this.kiteVX += (g.vx * force) * dt;
                this.kiteVY += (g.vy * force - 0.05) * dt; // mild lift
            }

            if (g.life >= g.maxLife) {
                this.gusts.splice(i, 1);
            }
        }

        // Drag
        this.kiteVX *= this.drag;
        this.kiteVY *= this.drag;

        // Slight random turbulence
        this.kiteVX += (Math.random() - 0.5) * 0.06 * dt;

        // Move kite
        this.kiteX += this.kiteVX * dt;
        this.kiteY += this.kiteVY * dt;

        // Angle from velocity
        this.kiteAngle = this.kiteVX * 3;

        // Bounce on ceiling instead of dying
        if (this.kiteY < 30) {
            this.kiteY = 30;
            this.kiteVY = Math.abs(this.kiteVY) * 0.6 + 2; // Bounce back down
        }

        // Lose if touching ANY edge (bottom, left, right)
        if (this.kiteY > this.H + 40 || this.kiteX < -30 || this.kiteX > this.W + 30) {
            this.triggerSnap();
        }

        this.draw();
        requestAnimationFrame(() => this.animLoop());
    }

    draw() {
        const ctx = this.ctx;
        ctx.clearRect(0, 0, this.W, this.H);

        // Draw blinking danger borders
        const blinkAlpha = 0.2 + (Math.sin(Date.now() / 120) * 0.5 + 0.5) * 0.3;
        ctx.save();
        ctx.strokeStyle = this.secondaryColor;
        ctx.lineWidth = 15;
        ctx.globalAlpha = blinkAlpha;
        ctx.beginPath();
        ctx.rect(0, 0, this.W, this.H);
        ctx.stroke();
        ctx.restore();

        // Draw background storm wind streaks
        const t = performance.now();
        ctx.save();
        ctx.fillStyle = this.secondaryColor;
        ctx.globalAlpha = 0.15;
        for (let i = 0; i < 6; i++) {
            const y = (i * this.H / 6) + Math.sin(t / 800 + i) * 30;
            const speed = this.crossWind * 12; // visual speed multiplier
            // Wrap around logic
            let x = ((t * speed + i * 300) % (this.W + 300));
            if (this.crossWind < 0) x = this.W - x;
            ctx.fillRect(x - 50, y, 60 + Math.random() * 40, 2);
        }
        ctx.restore();

        // Draw gusts (air circles fading)
        for (const g of this.gusts) {
            const alpha = Math.max(0, 0.3 * (1 - g.life / g.maxLife));
            ctx.save();
            ctx.globalAlpha = alpha;
            ctx.strokeStyle = this.secondaryColor;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.arc(g.x, g.y, g.radius, 0, Math.PI * 2);
            ctx.stroke();

            // Small wind lines inside
            const angle = Math.atan2(g.vy, g.vx);
            for (let j = 0; j < 3; j++) {
                const lx = g.x + (Math.random() - 0.5) * g.radius;
                const ly = g.y + (Math.random() - 0.5) * g.radius * 0.5;
                ctx.beginPath();
                ctx.moveTo(lx, ly);
                ctx.lineTo(lx + Math.cos(angle) * 12, ly + Math.sin(angle) * 12);
                ctx.stroke();
            }
            ctx.restore();
        }

        // Draw string from kite to bottom center
        ctx.save();
        ctx.strokeStyle = this.secondaryColor;
        ctx.globalAlpha = 0.5;
        ctx.lineWidth = 1.5;
        const stringBaseX = this.W * 0.5;
        const stringBaseY = this.H + 20;
        
        if (!this.stringSnapped) {
            const midX = (this.kiteX + stringBaseX) / 2 + Math.sin(Date.now() / 300) * 8;
            const midY = (this.kiteY + stringBaseY) / 2 + 30;
            ctx.beginPath();
            ctx.moveTo(this.kiteX, this.kiteY + 30);
            ctx.quadraticCurveTo(midX, midY, stringBaseX, stringBaseY);
            ctx.stroke();
        } else {
            // Snapped string fluttering in the wind
            ctx.beginPath();
            ctx.moveTo(this.kiteX, this.kiteY + 30);
            const flut = Date.now() / 100;
            // The string flies in the direction of the crosswind
            const dir = Math.sign(this.crossWind);
            ctx.quadraticCurveTo(this.kiteX + dir * 20 + Math.sin(flut) * 10, this.kiteY + 60, this.kiteX + dir * 40 + Math.sin(flut * 1.5) * 15, this.kiteY + 90);
            ctx.stroke();
        }
        ctx.restore();

        // Draw kite (diamond)
        ctx.save();
        ctx.translate(this.kiteX, this.kiteY);
        ctx.rotate(this.kiteAngle * Math.PI / 180);

        // Shadow
        ctx.globalAlpha = 0.15;
        ctx.fillStyle = this.primaryColor;
        ctx.beginPath();
        ctx.moveTo(2, -30);
        ctx.lineTo(25, 0);
        ctx.lineTo(2, 40);
        ctx.lineTo(-21, 0);
        ctx.closePath();
        ctx.fill();

        // Kite body
        ctx.globalAlpha = 1;
        ctx.fillStyle = this.primaryColor;
        ctx.beginPath();
        ctx.moveTo(0, -32);
        ctx.lineTo(23, -2);
        ctx.lineTo(0, 38);
        ctx.lineTo(-23, -2);
        ctx.closePath();
        ctx.fill();

        // Cross struts
        ctx.strokeStyle = this.secondaryColor;
        ctx.lineWidth = 2;
        ctx.globalAlpha = 0.5;
        ctx.beginPath();
        ctx.moveTo(0, -32);
        ctx.lineTo(0, 38);
        ctx.moveTo(-23, -2);
        ctx.lineTo(23, -2);
        ctx.stroke();

        // Kite highlight
        ctx.globalAlpha = 0.2;
        ctx.fillStyle = this.secondaryColor;
        ctx.beginPath();
        ctx.moveTo(0, -32);
        ctx.lineTo(23, -2);
        ctx.lineTo(0, -2);
        ctx.closePath();
        ctx.fill();

        // Tail ribbons
        ctx.globalAlpha = 0.7;
        const tRibbon = Date.now() / 200;
        ctx.strokeStyle = this.primaryColor;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(0, 38);
        for (let i = 1; i <= 5; i++) {
            ctx.lineTo(Math.sin(tRibbon + i * 1.2) * 10, 38 + i * 12);
        }
        ctx.stroke();

        ctx.strokeStyle = this.secondaryColor;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(-5, 38);
        for (let i = 1; i <= 4; i++) {
            ctx.lineTo(-5 + Math.sin(tRibbon + i * 1.5 + 1) * 8, 38 + i * 10);
        }
        ctx.stroke();

        ctx.restore();
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
        this.engine.input.element.removeEventListener('pointermove', this.onMove);
    }
}
